/**
 * Requested skills for a resource request, rendered as badges.
 *
 * When the source system sent the required/preferred breakdown the skills are
 * shown in two groups, since a required skill is a far stronger ask than a
 * preferred one. Older requests carry names only, and render as a single
 * ungrouped list exactly as before.
 */

import { Badge } from '@/components/ui/badge';
import { splitRequestedSkills } from '@/lib/recommendationEngine';
import type { RequestedSkill } from '@/types/resourceRequest';

/** Section heading for the skills block, matching what the request actually carries. */
export function skillsSectionLabel(skillsDetailed?: RequestedSkill[]): string {
  return skillsDetailed && skillsDetailed.length > 0 ? 'Skills Requested' : 'Required Skills';
}

function SkillGroup({ label, names, variant }: {
  label: string;
  names: string[];
  variant: 'secondary' | 'outline';
}) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground mb-1">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {names.map(name => (
          <Badge key={name} variant={variant} className="text-xs px-2 py-0.5 font-normal">{name}</Badge>
        ))}
      </div>
    </div>
  );
}

export function RequestedSkillBadges({ skills, skillsDetailed }: {
  skills: string[];
  skillsDetailed?: RequestedSkill[];
}) {
  if (!skillsDetailed || skillsDetailed.length === 0) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {skills.map(name => (
          <Badge key={name} variant="secondary" className="text-xs px-2 py-0.5 font-normal">{name}</Badge>
        ))}
      </div>
    );
  }

  const { required, preferred } = splitRequestedSkills(skills, skillsDetailed);

  return (
    <div className="space-y-2">
      {required.length > 0 && (
        <SkillGroup label="Required" names={required} variant="secondary" />
      )}
      {preferred.length > 0 && (
        <SkillGroup label="Preferred" names={preferred} variant="outline" />
      )}
    </div>
  );
}
